import styled, { keyframes } from "styled-components";
import { COLORS, BRAND_C } from "../../common/colors";

// ─── Design tokens — pulled from centralized colors.js ────────────────────────
const C = {
  brand:      COLORS.primary,        // Sage Forest  #3d6b5e
  brandLight: COLORS.primaryLight,   // Eucalyptus   #7ab5a0
  brandDark:  COLORS.primaryDark,    // Deep Forest  #2b4a42
  accent:     COLORS.accent,         // Warm Sand    #c9a96e
  surface:    COLORS.white,          // Parchment    #fdfaf6
  surfaceAlt: COLORS.bgCool,         // Sage Mist    #e8f0ee
  border:     COLORS.border,         // green-tinted border
  text:       COLORS.textDark,       // Deep Forest  #2b4a42
  textMid:    COLORS.textBody,       // Bark Brown   #4a3728
  textSoft:   COLORS.textMid,        // Stone Sage   #8fa89e
  muted:      COLORS.bgLight,        // Pale Jade    #d4e8e1
  bg:         COLORS.pageBg,         // Parchment    #fdfaf6
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Wave ─────────────────────────────────────────────────────────────────────
export const WaveWrap = styled.div`
  line-height: 0;
  overflow: hidden;
  margin-bottom: -1px;
  pointer-events: none;
  user-select: none;
`;

// ─── Shell ────────────────────────────────────────────────────────────────────
export const FooterRoot = styled.footer`
  background: linear-gradient(160deg, ${COLORS.primaryDark} 0%, #1a2e28 100%);
  position: relative;
  overflow: hidden;

  /* Dot grid */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, ${COLORS.primaryTint} 1px, transparent 1px);
    background-size: 28px 28px;
    mask-image: linear-gradient(to bottom, transparent, rgba(0,0,0,0.4) 20%, rgba(0,0,0,0.4) 80%, transparent);
    pointer-events: none;
  }

  /* Decorative blob top-right */
  &::after {
    content: "";
    position: absolute;
    width: 480px; height: 480px;
    background: ${COLORS.primaryGlow};
    border-radius: 50%;
    filter: blur(80px);
    top: -120px; right: -120px;
    pointer-events: none;
  }
`;

export const FooterBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  pointer-events: none;
  &.fb1 { width: 360px; height: 360px; background: ${COLORS.accentTint}; bottom: -80px; left: -80px; }
  &.fb2 { width: 240px; height: 240px; background: ${COLORS.primaryTint}; top: 40%; left: 30%; }
`;

export const FooterInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 56px 32px 0;

  @media (max-width: 48em) { padding: 44px 20px 0; }
`;

// ─── Top grid ─────────────────────────────────────────────────────────────────
export const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 40px 56px;
  padding-bottom: 44px;
  border-bottom: 1px solid ${COLORS.borderGreen20};

  @media (max-width: 56em) {
    grid-template-columns: 1fr 1fr;
    gap: 36px 32px;
    & > *:first-child { grid-column: 1 / -1; }
  }

  @media (max-width: 36em) {
    grid-template-columns: 1fr 1fr;
    gap: 28px 20px;
    & > *:first-child { grid-column: 1 / -1; }
  }
`;

// ─── Brand col ────────────────────────────────────────────────────────────────
export const BrandCol = styled.div`
  animation: ${fadeUp} 0.6s ease both;
`;

export const BrandLogoWrap = styled.div`
  margin-bottom: 16px;
  filter: brightness(0) invert(1) opacity(0.92);
  img, svg { max-width: 160px; height: auto; }
`;

export const BrandAccentBar = styled.div`
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent});
  border-radius: 2px;
  margin-bottom: 14px;
`;

export const BrandDesc = styled.p`
  font-size: 0.875rem;
  color: rgba(255,255,255,0.50);
  line-height: 1.75;
  max-width: 320px;
  margin-bottom: 24px;

  @media (max-width: 36em) {
    max-width: 100%;
    font-size: 0.84rem;
  }
`;

// ─── Social icons ─────────────────────────────────────────────────────────────
export const SocialRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

export const SocialBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(61, 107, 94, 0.20);
  border: 1.5px solid rgba(61, 107, 94, 0.35);
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s, color 0.2s, transform 0.2s;

  svg {
    width: 18px;
    height: 18px;
    display: block;
    fill: currentColor;
  }

  &:hover {
    background: ${COLORS.primary};
    border-color: ${COLORS.primaryLight};
    color: #fff;
    transform: translateY(-3px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

// ─── Link columns ─────────────────────────────────────────────────────────────
export const LinkCol = styled.div`
  animation: ${fadeUp} 0.6s ease both;
  animation-delay: ${({ $delay }) => $delay || "0s"};
`;

export const ColTitle = styled.h4`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${COLORS.accent};
  margin-bottom: 18px;
`;

export const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const LinkBtn = styled.button`
  background: none;
  border: none;
  padding: 7px 10px 7px 0;
  font-size: 0.875rem;
  color: rgba(255,255,255,0.55);
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  width: 100%;
  transition: color 0.2s, padding-left 0.22s;
  border-radius: 6px;

  &::before {
    content: "";
    width: 0;
    height: 1.5px;
    background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent});
    border-radius: 1px;
    margin-right: 0;
    transition: width 0.22s, margin-right 0.22s;
    flex-shrink: 0;
  }

  &:hover {
    color: #fff;
    padding-left: 4px;
    &::before { width: 14px; margin-right: 8px; }
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// ─── Bottom bar ───────────────────────────────────────────────────────────────
export const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 18px 0 24px;
  position: relative;
  z-index: 1;

  @media (max-width: 48em) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
    padding: 16px 0 24px;
  }
`;

export const CopyrightText = styled.p`
  font-size: 0.78rem;
  color: rgba(255,255,255,0.30);
  line-height: 1.6;

  a {
    color: ${COLORS.primaryLight};
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s;
    &:hover { color: ${COLORS.accent}; }
  }
`;

export const LegalRow = styled.nav`
  display: flex;
  gap: 0;
  flex-wrap: wrap;
  justify-content: center;
`;

export const LegalBtn = styled.button`
  background: none;
  border: none;
  padding: 4px 10px;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.30);
  cursor: pointer;
  transition: color 0.2s;
  position: relative;

  &:not(:last-child)::after {
    content: "·";
    position: absolute;
    right: -1px;
    color: rgba(255,255,255,0.18);
  }

  &:hover { color: rgba(255,255,255,0.75); }
  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;